"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, Calendar, ArrowRight, ShieldCheck, FileCheck2, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const features = [{
  title: "Fast tender response",
  description: "Detailed take-offs and quotes returned promptly.",
  icon: Clock
}, {
  title: "Site technical surveys",
  description: "On-site assessment for loading, access requirements, and permits.",
  icon: Calendar
}, {
  title: "Verified standards",
  description: "Fully compliant with NASC and CHAS requirements.",
  icon: ShieldCheck
}];
export default function Quote() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="request-a-quote" data-nav="dark" className="dark relative overflow-hidden bg-background text-foreground py-20 md:py-28 border-t border-border">
      {/* Structural Datum Rule / Ambient Accent Background */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-px bg-linear-to-r from-transparent via-primary to-transparent" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 size-96 rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="mx-auto max-w-4xl text-center">
          {/* Eyebrow / Category Tag */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: "easeOut"
        }} className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3.5 py-1 text-xs font-semibold tracking-wider uppercase text-muted-foreground mb-6">
            <span className="size-2 rounded-full bg-primary" />
            <span>Commercial Estimating & Site Surveys</span>
          </motion.div>

          {/* Heading */}
          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: 0.08,
          ease: "easeOut"
        }} className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl font-default">
            Plan your next commercial build.
          </motion.h2>

          {/* Supporting Copy */}
          <motion.p initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: 0.16,
          ease: "easeOut"
        }} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Send your tender drawings for a prompt quote, or arrange an on-site survey with our team across Berkshire, Surrey, Hampshire, Buckinghamshire, and London.
          </motion.p>

          {/* Action Trio Buttons */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: 0.24,
          ease: "easeOut"
        }} className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6 shadow-sm">
              <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                <FileCheck2 className="size-4" />
                <span>Request a Quote</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto border border-border bg-secondary text-secondary-foreground hover:bg-secondary/80 font-medium px-6">
              <a href="tel:01344929900" className="inline-flex items-center gap-2 text-secondary-foreground">
                <Phone className="size-4 text-primary" />
                <span>Call Now</span>
              </a>
            </Button>

            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto border-border bg-background/50 hover:bg-muted text-foreground font-medium px-6">
              <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                <span>Discuss a Project</span>
              </Link>
            </Button>
          </motion.div>

          {/* Quick Direct Line Notice */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: 0.32,
          ease: "easeOut"
        }} className="mt-4 text-xs sm:text-sm text-muted-foreground flex items-center justify-center gap-2">
            <span>Direct estimating desk:</span>
            <a href="tel:01344929900" className="font-medium text-foreground hover:text-primary underline underline-offset-4 decoration-border hover:decoration-primary transition-colors">
              01344 929900
            </a>
            <span className="text-border">|</span>
            <span>Monday to Friday, 8am - 6pm</span>
          </motion.div>
        </div>

        {/* Feature & Credential Highlights */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 pt-12 border-t border-border">
          {features.map((feature, i) => {
          const IconComponent = feature.icon;
          return <motion.div key={feature.title} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.1 * i,
            ease: "easeOut"
          }} className="flex flex-col items-start rounded-lg border border-border/80 bg-secondary/30 p-5 transition-[border-color,background-color] hover:border-border hover:bg-secondary/50">
                <div className="flex items-center gap-3">
                  <div className="flex size-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <IconComponent className="size-4.5" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground font-default">
                    {feature.title}
                  </h3>
                </div>
                <p className="mt-2.5 text-sm text-muted-foreground leading-normal">
                  {feature.description}
                </p>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}